import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';

/**
 * Landing page for an invitation link (/app/accept-invite?token=…).
 *
 * The route sits inside the authenticated area on purpose: an invitee arriving from the email is shown
 * the sign-in screen first, creates a password or signs in with the invited address, and lands back here
 * with the token still in the query string. Only then can POST /invites/accept work — it identifies the
 * caller from their Cognito token and refuses if the email doesn't match the invite.
 *
 * Before this existed the invite email had nowhere to send anyone, which is half of why invitations never
 * worked: no email was sent, and no page would have accepted the token if it had been.
 */
export function AcceptInvite({ onDone }: { onDone?: (() => void | Promise<void>) | undefined } = {}) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [state, setState] = useState<'review' | 'working' | 'done' | 'failed'>(token ? 'review' : 'failed');

  /*
   * What they are joining, fetched before anything is accepted.
   *
   * This page used to accept on mount, so the first thing an invitee knew about the decision was that it
   * had already been made for them — no sight of which organisation they were joining, no terms, no chance
   * to stop. It shows the invitation now and waits.
   */
  const preview = useQuery({
    queryKey: ['invite-preview', token],
    queryFn: () => api.get<{
      email: string; role: string; accountName: string;
      physicalAddress?: { line1?: string; city?: string; state?: string; postalCode?: string } | null;
    }>(`/invites/preview?token=${encodeURIComponent(token)}`),
    enabled: Boolean(token),
    retry: false,
  });

  async function accept() {
    setState('working');
    setErr(null);
    try {
      await api.post('/invites/accept', { token, acceptedTerms: true });
      setState('done');
      /*
       * Clear the token from the URL BEFORE the claims refresh, not after.
       *
       * The moment this user counts as a member the provisioning gate stands down and /app/accept-invite
       * renders as an ordinary route again — where the invite it looks up is no longer PENDING. Leaving the
       * URL in place would make the invitee's reward for joining a red "we couldn't open this invitation".
       * When the gate is what rendered us, this only rewrites the address bar; the confirmation below stays
       * on screen until the new claims arrive.
       */
      navigate('/app', { replace: true });
      // The account context gates the whole app; drop it so everything refetches as a MEMBER.
      await qc.invalidateQueries();
      await onDone?.();
    } catch (e) {
      setState('failed');
      setErr(e instanceof ApiError ? e.message : 'This invitation could not be accepted.');
    }
  }

  if (state === 'working') {
    return <div className="loading-screen">// joining the account…</div>;
  }
  if (state === 'done') {
    return (
      <div className="panel empty">
        <div className="support-check">✓</div>
        <h3>You're in</h3>
        <p>Taking you to your dashboard…</p>
      </div>
    );
  }
  if (state === 'review') {
    if (preview.isPending) return <div className="loading-screen">// loading your invitation…</div>;
    if (preview.isError) {
      return (
        <div className="panel empty">
          <h3>We couldn't open this invitation</h3>
          <p>{preview.error instanceof ApiError ? preview.error.message : 'This invitation is not valid.'}</p>
          <Link to="/app" className="track-link">Continue to EFTCreator</Link>
        </div>
      );
    }
    const p = preview.data;
    const addr = p.physicalAddress;
    const where = addr
      ? [addr.line1, [addr.city, addr.state].filter(Boolean).join(', '), addr.postalCode].filter(Boolean).join(' · ')
      : '';
    return (
      <div className="panel invite-accept">
        <h3>Join {p.accountName}</h3>
        {/*
          * Shown, not asked for. The organisation and its address already exist on the account that invited
          * them — an invitee filling those in would be describing someone else's business, and the form that
          * used to appear here would have created a SECOND account rather than joining this one.
          */}
        <dl className="invite-facts">
          <div><dt>Organization</dt><dd>{p.accountName}</dd></div>
          {where && <div><dt>Address</dt><dd>{where}</dd></div>}
          <div><dt>You'll join as</dt><dd>{p.role.replace(/([a-z])([A-Z])/g, '$1 $2')}</dd></div>
          <div><dt>Your sign-in</dt><dd className="mono">{p.email}</dd></div>
        </dl>

        <label className="checkbox-row">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>
            I agree to the{' '}
            <a href="/legal/terms" target="_blank" rel="noreferrer">Terms of Use</a> and{' '}
            <a href="/legal/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
          </span>
        </label>

        {err && <p className="cart-error">{err}</p>}
        <button className="btn btn-primary" disabled={!agreed} onClick={() => void accept()}>
          Join {p.accountName}
        </button>
      </div>
    );
  }
  return (
    <div className="panel empty">
      <h3>We couldn't accept this invitation</h3>
      <p>{err}</p>
      <p className="field-help">
        Invitations expire, and can be cancelled or already used. They also only work for the exact email
        address they were sent to — if you signed in with a different one, sign out and try again with the
        invited address. Otherwise ask whoever invited you to send a new invitation.
      </p>
      <Link to="/app" className="track-link">Continue to EFTCreator</Link>
    </div>
  );
}
